// Imperative compositor — called from useScreenRecording's startScreenCapture so the composed
// stream is available synchronously before MediaRecorder is constructed.
// stop() tears down the RAF loop and canvas capture track; caller owns the source stream tracks.
// RAF is suspended when document.hidden to reduce CPU load on hidden tabs.


import { positionToXY, SIZE_MAP, CANVAS_W, CANVAS_H } from "../hooks/use-screen-share-composer";
import type { PipPosition, PipSize } from "../hooks/use-screen-share-composer";

export interface ScreenShareCompositionOptions {
  screenStream: MediaStream;
  cameraStream: MediaStream;
  micStream: MediaStream | null;
  position?: PipPosition;
  size?: PipSize;
  fps?: number;
}

export interface ScreenShareComposition {
  stream: MediaStream;
  stop: () => void;
}

export function createScreenShareCompositionImperative({
  screenStream,
  cameraStream,
  micStream,
  position = "BR",
  size = "M",
  fps = 30,
}: ScreenShareCompositionOptions): ScreenShareComposition {
  const canvas = document.createElement("canvas");
  canvas.width = CANVAS_W;
  canvas.height = CANVAS_H;
  const ctx = canvas.getContext("2d")!;

  const screenVideoEl = document.createElement("video");
  screenVideoEl.srcObject = screenStream;
  screenVideoEl.muted = true;
  screenVideoEl.playsInline = true;

  const cameraVideoEl = document.createElement("video");
  cameraVideoEl.srcObject = cameraStream;
  cameraVideoEl.muted = true;
  cameraVideoEl.playsInline = true;

  const captureStream = canvas.captureStream(fps);

  screenStream.getAudioTracks().forEach((t) => captureStream.addTrack(t));
  cameraStream.getAudioTracks().forEach((t) => captureStream.addTrack(t));
  if (micStream) {
    micStream.getAudioTracks().forEach((t) => captureStream.addTrack(t));
  }

  let rafId: number | null = null;
  let stopped = false;

  const draw = () => {
    if (stopped) return;
    // Suspend RAF when tab is hidden to avoid wasting CPU on invisible frames.
    if (document.hidden) return;

    ctx.drawImage(screenVideoEl, 0, 0, CANVAS_W, CANVAS_H);

    if (cameraVideoEl.readyState >= 2) {
      const [pipW, pipH] = SIZE_MAP[size];
      // margin=0: Angular places the overlay flush against the screen edge (no padding).
      const [x, y] = positionToXY(position, pipW, pipH, CANVAS_W, CANVAS_H, 0);
      ctx.drawImage(cameraVideoEl, x, y, pipW, pipH);
    }

    rafId = requestAnimationFrame(draw);
  };

  const handleVisibilityChange = () => {
    if (!document.hidden && !stopped) {
      rafId = requestAnimationFrame(draw);
    }
  };
  document.addEventListener("visibilitychange", handleVisibilityChange);

  void screenVideoEl.play().catch(() => {});
  void cameraVideoEl.play().catch(() => {});
  rafId = requestAnimationFrame(draw);

  const stop = () => {
    if (stopped) return;
    stopped = true;

    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    document.removeEventListener("visibilitychange", handleVisibilityChange);

    // Stop canvas capture track to signal MediaRecorder end-of-stream. Source tracks are NOT
    // stopped here — caller owns them.
    captureStream.getVideoTracks().forEach((t) => t.stop());

    screenVideoEl.srcObject = null;
    cameraVideoEl.srcObject = null;
  };

  return { stream: captureStream, stop };
}
